/*
 * $Id:: index.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useState } from 'react';

import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import VisibilityOffOutlined from '@material-ui/icons/VisibilityOffOutlined';
import VisibilityOutlined from '@material-ui/icons/VisibilityOutlined';

import inputsMaxChars from '../../../../app/inputsMaxChars';
import { t } from '../../../../providers';

const useStyles = makeStyles((theme) => ({
    resetPasswordWrapper: {
        display: 'flex',
        flexFlow: 'column nowrap',
    },
    inputWrapper: {
        marginBottom: '1.5rem',
    },
    textInput: {
        height: '2.5rem',
        width: '14rem',
        marginTop: '10px',
        '@media (max-width:600px)': {
            width: '12.5rem',
        },
    },
    inputItem: {
        marginBottom: '10px',
        flex: '1 1 auto',
    },
    inputError: {
        marginTop: '5px',
    },
    fieldLabel: {
        fontWeight: theme.typography.titleFontWeight,
        fontSize: theme.typography.titleFontSize,
        color: theme.palette.text.mainGreen,
    },
    loginButton: {
        width: '11.75rem',
        boxShadow: 'none',
        margin: '0',
        height: '2.5rem',
        '@media (max-width:600px)': {
            minWidth: '11rem',
            height: '3rem',
        },
    },
    rPInfoMessage: {
        maxWidth: '610px',
        marginBottom: '15px',
    },
    rPInfoMessageUndelined: {
        maxWidth: '610px',
        marginBottom: '15px',
        textDecoration: 'underline',
    },
    backButton: {
        width: '3.5rem',
        margin: '0',
        marginRight: '1.25rem',
    },
    resetButton: {
        minWidth: '151px',
        margin: '0',
    },
}));

const ResetPasswordForm = ({ setNewPassword, error, loading }) => {
    const classes = useStyles();

    const [password, setPassword] = useState({ value: '', isDirty: false, showPassword: false, isBlured: false });
    const [confirmPassword, setConfirmPassword] = useState({
        value: '',
        isDirty: false,
        showPassword: false,
        isBlured: false,
    });
    const [passwordsMatch, setPasswordsMatch] = useState(false);

    const handlePasswordChange = (event) => {
        const value = event.target.value;
        if (!inputsMaxChars.password || value.length <= inputsMaxChars.password) {
            setPassword({ ...password, value, isDirty: true });
            setPasswordsMatch(event.target.value === confirmPassword.value);
        }
    };

    const handleConfirmPasswordChange = (event) => {
        if (!inputsMaxChars.password || event.target.value.length <= inputsMaxChars.password) {
            setConfirmPassword({ ...password, value: event.target.value, isDirty: true });
            setPasswordsMatch(event.target.value === password.value);
        }
    };

    const handleOnBlurPassword = () => {
        if (!password.isBlured) setPassword({ ...password, isBlured: true });
    };

    const handleOnBlurConfirmPassword = () => {
        if (!confirmPassword.isBlured) setConfirmPassword({ ...confirmPassword, isBlured: true });
    };

    const submitPassword = (event) => {
        event.preventDefault();
        if (!password.value || !passwordsMatch) {
            setPassword({ ...password, isDirty: true, isBlured: true });
            setConfirmPassword({ ...confirmPassword, isDirty: true, isBlured: true });
            return;
        }
        setNewPassword(password.value);
    };

    const toggleVisibility = () => {
        setPassword({ ...password, showPassword: !password.showPassword });
    };

    const toggleConfirmVisibility = () => {
        setConfirmPassword({ ...confirmPassword, showPassword: !confirmPassword.showPassword });
    };

    const passwordsDoNotMatch = !!password.value && password.isBlured && confirmPassword.isBlured && password.value !== confirmPassword.value;

    const resetPasswordForm = (
        <div className={classes.resetPasswordWrapper}>
            <form onSubmit={submitPassword} noValidate>
                <Grid container direction={'column'} className={classes.inputWrapper}>
                    <Grid item xs sm md className={classes.inputItem}>
                        <InputLabel
                            htmlFor={'password'}
                            role="input"
                            error={(password.isDirty && !password.value) || passwordsDoNotMatch}
                            className={classes.fieldLabel}>
                            {t('new.password')}*
                        </InputLabel>
                        <Input
                            id={'password'}
                            role="input"
                            className={classes.textInput}
                            placeholder={t('type.password')}
                            type={password.showPassword ? 'text' : 'password'}
                            name={'password'}
                            fullWidth
                            autoFocus
                            onChange={handlePasswordChange}
                            onBlur={handleOnBlurPassword}
                            value={password.value}
                            endAdornment={
                                <IconButton onClick={toggleVisibility} type={'button'} disableRipple>
                                    {password.showPassword ? (
                                        <VisibilityOffOutlined aria-hidden="true" focusable="false" />
                                    ) : (
                                        <VisibilityOutlined aria-hidden="true" focusable="false" />
                                    )}
                                </IconButton>
                            }
                            error={(password.isDirty && !password.value) || passwordsDoNotMatch}
                        />
                        {password.isDirty && !password.value && (
                            <Typography variant={'body2'} color={'error'} className={classes.inputError}>
                                {t('required.field')}
                            </Typography>
                        )}
                        {passwordsDoNotMatch && (
                            <Typography variant={'body2'} color={'error'} className={classes.inputError}>
                                {t('entering.same.value')}
                            </Typography>
                        )}
                    </Grid>
                    <Grid item>
                        <Typography variant={'body2'} className={classes.rPInfoMessageUndelined}>
                            {t('password.pattern.description')}
                        </Typography>
                    </Grid>
                    <Grid item xs sm md className={classes.inputItem}>
                        <InputLabel
                            htmlFor={'confirmPassword'}
                            role="input"
                            error={(confirmPassword.isDirty && !confirmPassword.value) || passwordsDoNotMatch}
                            className={classes.fieldLabel}>
                            {t('repeat.new.password')}*
                        </InputLabel>
                        <Input
                            id={'confirmPassword'}
                            role="input"
                            className={classes.textInput}
                            placeholder={t('type.password')}
                            type={confirmPassword.showPassword ? 'text' : 'password'}
                            name={'confirmPassword'}
                            fullWidth
                            onChange={handleConfirmPasswordChange}
                            onBlur={handleOnBlurConfirmPassword}
                            value={confirmPassword.value}
                            endAdornment={
                                <IconButton onClick={toggleConfirmVisibility} type="button" disableRipple>
                                    {confirmPassword.showPassword ? (
                                        <VisibilityOffOutlined aria-hidden="true" focusable="false" />
                                    ) : (
                                        <VisibilityOutlined aria-hidden="true" focusable="false" />
                                    )}
                                </IconButton>
                            }
                            error={(confirmPassword.isDirty && !confirmPassword.value) || passwordsDoNotMatch}
                        />
                        {confirmPassword.isDirty && !confirmPassword.value && (
                            <Typography variant={'body2'} color={'error'} className={classes.inputError}>
                                {t('required.field')}
                            </Typography>
                        )}
                    </Grid>
                    <Grid item>
                        <Typography variant={'body2'} className={classes.rPInfoMessage}>
                            {t('make.sure.matches')}
                        </Typography>
                    </Grid>
                    {!!error && (
                        <Grid item xs sm md>
                            <Typography color={'error'} className={classes.rPInfoMessage}>
                                {t(error)}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
                <Grid container spacing={0}>
                    <Grid item>
                        <Button variant={'contained'} className={classes.resetButton} type={'submit'}>
                            {t('set.new.password')}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </div>
    );

    return loading ? <CircularProgress /> : resetPasswordForm;
};

export default ResetPasswordForm;
